import React, { useEffect, useState } from "react";
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  ArrowLeftIcon,
  FunnelIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/24/outline";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress
} from "@mui/material";
import {
  getCandidatesByBranch, approveCandidate,
} from "../Api/candidateApi";
import {
  getAllProjects, getProgramsByProject, getAllProgramsByProject,
  getDashboardData, getCenterDetail
} from "../Api/projectApi";
import { getAllBranches } from "../Api/branches";
import { useNavigate, useLocation } from "react-router-dom";
import { PieChart } from "@mui/x-charts/PieChart";
import { LineChart } from "@mui/x-charts/LineChart";
import toast from "react-hot-toast";

// Theme / Color constants
const BRAND_PRIMARY = "#2BB5CE";
const BRAND_DARK = "#16879A";
const BG_COLOR = "#F7F9FB";
const CARD_BG = "#FFFFFF";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#667085";
const BORDER_COLOR = "#E5E7EB";
const COLOR_SUCCESS = "#16A34A";
const COLOR_WARNING = "#F59E0B";
const COLOR_DANGER = "#DC2626";

const PROJECT_COLORS = {
  "Shriram Finance": { bg: "#FBF3DE", border: "#C99A1E", accent: "#C99A1E", text: "#6B4E00" },
  Mahindra:         { bg: "#FCE9EB", border: "#C2263A", accent: "#C2263A", text: "#7A0018" },
  Linde:            { bg: "#E7EEF9", border: "#173E6E", accent: "#173E6E", text: "#173E6E" },
};
const DEFAULT_PROJECT_COLOR = { bg: "#EEF1F6", border: "#3A4356", accent: "#3A4356", text: "#1B2334" };
const PROJECT_NORMALIZE_MAP = {
  "Linde Hazmat": "Linde",
  "Linde Upskilling": "Linde",
  "SSSF Upskilling": "Shriram Finance",
};

// Reusable duration formatter
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) return "00:00";
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  } else {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
};

const styles = `
  .dashboard-container {
    background-color: ${BG_COLOR};
    color: ${TEXT_PRIMARY};
    font-family: 'Inter', sans-serif;
    min-height: 100vh;
  }
  .premium-card {
    background: ${CARD_BG};
    border: 1px solid ${BORDER_COLOR};
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(17, 24, 39, 0.03);
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .premium-card:hover {
    border-color: #CBD5E1;
    box-shadow: 0 4px 12px rgba(17, 24, 39, 0.05);
  }
  .badge-status {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
  }
  .badge-active {
    background-color: #DCFCE7;
    color: ${COLOR_SUCCESS};
    border: 1px solid #BBF7D0;
  }
  .badge-idle {
    background-color: #FEF3C7;
    color: ${COLOR_WARNING};
    border: 1px solid #FDE68A;
  }
  .badge-offline {
    background-color: #F3F4F6;
    color: ${TEXT_SECONDARY};
    border: 1px solid #E5E7EB;
  }
  .custom-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }
  .custom-table th {
    background: #FAFAFA;
    color: ${TEXT_SECONDARY};
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 12px 16px;
    border-bottom: 1px solid ${BORDER_COLOR};
  }
  .custom-table td {
    padding: 14px 16px;
    border-bottom: 1px solid ${BORDER_COLOR};
    font-size: 13.5px;
    color: ${TEXT_PRIMARY};
  }
  .custom-table tr:last-child td {
    border-bottom: none;
  }
  .custom-table tr:hover td {
    background: #FAFBFC;
  }
  .trend-filter-btn {
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    border: 1px solid ${BORDER_COLOR};
    background: ${CARD_BG};
    color: ${TEXT_SECONDARY};
    transition: all 0.15s;
    cursor: pointer;
  }
  .trend-filter-btn.active {
    background: ${BRAND_PRIMARY};
    border-color: ${BRAND_PRIMARY};
    color: #FFFFFF;
  }
  .trend-filter-btn:hover:not(.active) {
    background: #F3F4F6;
    color: ${TEXT_PRIMARY};
  }
  .sparkline-box {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 24px;
  }
  .sparkline-bar {
    width: 3px;
    background: ${BRAND_PRIMARY}33;
    border-radius: 1px;
    transition: height 0.3s;
  }
  .sparkline-bar.active {
    background: ${BRAND_PRIMARY};
  }
`;

const Dashboard = () => {
  const [view, setView] = useState("dashboard");
  const [previousView, setPreviousView] = useState(null);
  const navigate = useNavigate();
  const locationState = useLocation().state;
  const role = localStorage.getItem("role");

  // Core metrics & state
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [centerDetailData, setCenterDetailData] = useState(null);

  // Enrollment Trend duration filter (7 Days / 30 Days)
  const [trendDays, setTrendDays] = useState(30);
  const [timeTrendDays, setTimeTrendDays] = useState(30);

  // Drilldown states
  const [projects, setProjects] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [branchCounts, setBranchCounts] = useState({});
  const [projectCounts, setProjectCounts] = useState({});
  const [programCounts, setProgramCounts] = useState({});
  const [allCenterCandidates, setAllCenterCandidates] = useState([]);
  const [centerCandidates, setCenterCandidates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [todayCount, setTodayCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [allProgramsByProject, setAllProgramsByProject] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Candidate Approval Dialog
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [approvalReason, setApprovalReason] = useState("");
  const [approvalCandidate, setApprovalCandidate] = useState(null);

  // Fetch Dashboard Stats from Backend
  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const res = await getDashboardData();
      if (res.data && res.data.success) {
        setDashboardStats(res.data.data);
      } else {
        toast.error("Failed to load dashboard statistics");
      }
    } catch (err) {
      console.error("Dashboard Stats Fetch Error:", err);
      toast.error("Unable to connect to analytics server");
    } finally {
      setLoading(false);
    }
  };

  // Fetch individual center detailed info (hourly chart, approved/pending)
  const fetchCenterDetailInfo = async (branchId) => {
    try {
      setLoadingDetail(true);
      const res = await getCenterDetail(branchId);
      if (res.data && res.data.success) {
        setCenterDetailData(res.data.data);
      } else {
        toast.error("Failed to load center details");
      }
    } catch (err) {
      console.error("Center Detail Fetch Error:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    
    // Check if redirecting to specific branch candidates view or daily logs
    if (locationState?.openPrograms) {
      handleAllProgramsClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState]);

  const changeView = (next) => {
    setPreviousView(view);
    setView(next);
    setSearchQuery("");
  };

  // Click handler for project item
  const handleProjectClick = async () => {
    try {
      setLoadingProjects(true);
      const res = await getAllProjects();
      setProjects([...new Set(res.data.map((p) => p.projectName))]);
      
      // Calculate project count counts from candidates
      const counts = {};
      if (dashboardStats?.projectChart) {
        dashboardStats.projectChart.forEach(p => {
          counts[p.label] = p.value;
        });
      }
      setProjectCounts(counts);
      changeView("projects");
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch projects list");
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleOpenProject = async (project) => {
    setSelectedProject(project);
    setSelectedProgram(null);
    const res = await getProgramsByProject(project);
    setPrograms(res.data);

    // Fetch program candidate counts
    const counts = {};
    if (dashboardStats?.centers) {
      // Map candidates based on branch records
      dashboardStats.centers.forEach(c => {
        counts[c.centerName] = (counts[c.centerName] || 0) + c.candidates;
      });
    }
    setProgramCounts(counts);
    changeView("programs");
  };

  const handleProgramClick = async (program) => {
    try {
      setLoadingLocations(true);
      const res = await getAllBranches();
      const filtered = res.data.data.filter(
        (b) => b.project === selectedProject && b.program === program
      );

      const results = await Promise.all(
        filtered.map(async (branch) => {
          const r = await getCandidatesByBranch(branch._id, 1, 1, true);
          return {
            branchId: branch._id,
            total: r.data?.total || 0,
            approved: r.data?.approved || 0,
          };
        })
      );

      const counts = {};
      results.forEach((r) => {
        counts[r.branchId] = { total: r.total, approved: r.approved };
      });
      setBranchCounts(counts);
      setSelectedProgram({ name: program, branches: filtered });
      changeView("locations");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleAllProgramsClick = async () => {
    try {
      const res = await getAllProgramsByProject();
      setAllProgramsByProject(res.data || {});
      changeView("allPrograms");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load programs");
    }
  };

  const handleCenterClick = async (branch) => {
    try {
      setSelectedBranch(branch);
      const res = await getCandidatesByBranch(branch._id || branch.branchId);
      const list = res.data.data || [];
      setAllCenterCandidates(list);
      setCenterCandidates(list);
      const today = new Date().toDateString();
      setTodayCount(list.filter((c) => new Date(c.createdAt).toDateString() === today).length);
      changeView("centerCandidates");
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch candidates for center");
    }
  };

  const handleTimeCenterSelect = (center) => {
    setSelectedBranch(center);
    fetchCenterDetailInfo(center.centerId);
  };

  useEffect(() => {
    if (!selectedDate) {
      setCenterCandidates(allCenterCandidates);
      return;
    }
    const picked = new Date(selectedDate).toDateString();
    setCenterCandidates(
      allCenterCandidates.filter((c) => new Date(c.createdAt).toDateString() === picked)
    );
  }, [selectedDate, allCenterCandidates]);

  // Filters candidates
  const filteredCandidates = centerCandidates.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.contactNumber?.toLowerCase().includes(q) ||
      c.location?.toLowerCase().includes(q)
    );
  });

  const filteredBranches = (selectedProgram?.branches || []).filter((b) => {
    const locOk = locationFilter ? b.location === locationFilter : true;
    const srchOk = searchQuery
      ? b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.location?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return locOk && srchOk;
  });

  const locationOptions = selectedProgram?.branches
    ? [...new Set(selectedProgram.branches.map((b) => b.location))]
    : [];

  // Sort centers by average registration processing time (fastest to slowest)
  const centersByTime = dashboardStats?.centers
    ? [...dashboardStats.centers].sort((a, b) => {
        // Handle 0 time as slowest
        if (a.averageTime === 0) return 1;
        if (b.averageTime === 0) return -1;
        return a.averageTime - b.averageTime;
      })
    : [];

  const candidateStatus = dashboardStats?.candidateStatus || { Approved: 0, Pending: 0, Rejected: 0 };

  // Determine status badge (ACTIVE, IDLE, OFFLINE) based on login/activity logs
  const getCenterStatus = (center) => {
    if (!center.loginTime) return { label: "OFFLINE", className: "badge-offline" };
    
    const lastActiveTime = new Date(center.lastActivity).getTime();
    const now = Date.now();
    const minsDiff = (now - lastActiveTime) / 60000;
    
    if (minsDiff <= 15) {
      return { label: "ACTIVE", className: "badge-active" };
    }
    return { label: "IDLE", className: "badge-idle" };
  };

  // Sparkline elements helper
  const renderSparkline = (dataList) => {
    if (!dataList || dataList.length === 0) return null;
    const maxVal = Math.max(...dataList, 1);
    return (
      <div className="sparkline-box">
        {dataList.map((val, idx) => (
          <div
            key={idx}
            className={`sparkline-bar ${idx === dataList.length - 1 ? 'active' : ''}`}
            style={{ height: `${Math.max(4, (val / maxVal) * 20)}px` }}
          />
        ))}
      </div>
    );
  };

  // Shimmer skeleton component
  const PremiumSkeleton = () => (
    <div className="space-y-6">
      {/* KPI skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="animate-pulse bg-white border border-gray-200 rounded-xl p-6 h-28" />
        ))}
      </div>
      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 animate-pulse bg-white border border-gray-200 rounded-xl p-6 h-[320px]" />
        <div className="animate-pulse bg-white border border-gray-200 rounded-xl p-6 h-[320px]" />
      </div>
      {/* Large table skeleton */}
      <div className="animate-pulse bg-white border border-gray-200 rounded-xl p-6 h-[260px]" />
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="dashboard-container px-6 py-4">

        {view !== "dashboard" && (
          <button
            className="flex items-center gap-2 mb-6 px-4 py-2 border border-gray-200 hover:border-gray-300 rounded-lg text-sm font-semibold bg-white text-gray-700 shadow-sm transition active:scale-95 cursor-pointer"
            onClick={() => {
              setView(previousView || "dashboard");
              setPreviousView(null);
              setSearchQuery("");
            }}
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-500" /> Back to Dashboard
          </button>
        )}

        {loading ? (
          <PremiumSkeleton />
        ) : (
          <>
            {/* ══════════ MAIN DASHBOARD VIEW ══════════ */}
            {view === "dashboard" && dashboardStats && (
              <div className="space-y-6">

                {/* 1. KPI SECTION */}
                <div className={role === "branchUser" ? "grid grid-cols-1" : "grid grid-cols-1 md:grid-cols-4 gap-4"}>
                  {/* Total Enrollments */}
                  <div className="premium-card p-5 flex flex-col justify-between cursor-pointer" onClick={() => navigate("/candidates")}>
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-bold tracking-wider text-gray-400 uppercase">Total Enrollments</span>
                      <UserGroupIcon className="w-5 h-5 text-[#2BB5CE]" />
                    </div>
                    <div className="flex justify-between items-end mt-4">
                      <div>
                        <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
                          {dashboardStats.totalCandidates.toLocaleString()}
                        </div>
                        <div className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1 mt-1">
                          <span>+{dashboardStats.todayCandidatesCount} new registrations today</span>
                        </div>
                      </div>
                      {renderSparkline(dashboardStats.enrollmentTrend?.slice(-7).map(d => d.count) || [])}
                    </div>
                  </div>

                  {role !== "branchUser" && (
                    <>
                      {/* Total Projects */}
                      <div className="premium-card p-5 flex flex-col justify-between cursor-pointer" onClick={handleProjectClick}>
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-bold tracking-wider text-gray-400 uppercase">Total Projects</span>
                          <BuildingOfficeIcon className="w-5 h-5 text-[#16879A]" />
                        </div>
                        <div className="mt-4">
                          <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            {dashboardStats.totalProjects}
                          </div>
                          <div className="text-[11px] font-semibold text-gray-500 mt-1">
                            Active operational partnerships
                          </div>
                        </div>
                      </div>

                      {/* Total Programs */}
                      <div className="premium-card p-5 flex flex-col justify-between cursor-pointer" onClick={handleAllProgramsClick}>
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-bold tracking-wider text-gray-400 uppercase">Total Programs</span>
                          <ClipboardDocumentListIcon className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div className="mt-4">
                          <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            {dashboardStats.totalPrograms}
                          </div>
                          <div className="text-[11px] font-semibold text-gray-500 mt-1">
                            Managed vocational courses
                          </div>
                        </div>
                      </div>

                      {/* Active Centers */}
                      <div className="premium-card p-5 flex flex-col justify-between">
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-bold tracking-wider text-gray-400 uppercase">Active Centers</span>
                          <CheckBadgeIcon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="mt-4">
                          <div className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            {dashboardStats.activeCentersCount}
                          </div>
                          <div className="text-[11px] font-semibold text-[#16879A] mt-1">
                            Centers online & active today
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {role === "branchUser" ? (
                  <div className="premium-card p-5 mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Enrollment Trend</h3>
                        <p className="text-xs text-gray-500">Daily registration volume progression</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className={`trend-filter-btn ${trendDays === 7 ? 'active' : ''}`}
                          onClick={() => setTrendDays(7)}
                        >
                          7 Days
                        </button>
                        <button
                          className={`trend-filter-btn ${trendDays === 30 ? 'active' : ''}`}
                          onClick={() => setTrendDays(30)}
                        >
                          30 Days
                        </button>
                      </div>
                    </div>

                    <div className="w-full h-[280px]">
                      {dashboardStats.enrollmentTrend?.length === 0 ? (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                          No enrollment trend data available
                        </div>
                      ) : (
                        <LineChart
                          height={260}
                          margin={{ top: 20, bottom: 40, left: 40, right: 20 }}
                          sx={{
                            "& .MuiChartsAxis-tickLabel": { fill: "#667085", fontSize: 10, fontFamily: "Inter" },
                            "& .MuiChartsAxis-line":      { stroke: "#E5E7EB" },
                            "& .MuiChartsAxis-tick":      { stroke: "#E5E7EB" },
                          }}
                          xAxis={[{
                            scaleType: "point",
                            data: (dashboardStats.enrollmentTrend || [])
                              .slice(-trendDays)
                              .map(d => {
                                const parts = d.date.split('-');
                                return `${parts[2]}/${parts[1]}`;
                              })
                          }]}
                          series={[{
                            data: (dashboardStats.enrollmentTrend || []).slice(-trendDays).map(d => d.count),
                            color: BRAND_PRIMARY,
                            label: "Candidates",
                            showMark: true,
                          }]}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* 2. ENROLLMENT TREND & PROJECT DISTRIBUTION */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Enrollment Trend */}
                  <div className="premium-card p-5 lg:col-span-2">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Enrollment Trend</h3>
                        <p className="text-xs text-gray-500">Daily registration volume progression</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className={`trend-filter-btn ${trendDays === 7 ? 'active' : ''}`}
                          onClick={() => setTrendDays(7)}
                        >
                          7 Days
                        </button>
                        <button
                          className={`trend-filter-btn ${trendDays === 30 ? 'active' : ''}`}
                          onClick={() => setTrendDays(30)}
                        >
                          30 Days
                        </button>
                      </div>
                    </div>

                    <div className="w-full h-[280px]">
                      {dashboardStats.enrollmentTrend?.length === 0 ? (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                          No enrollment trend data available
                        </div>
                      ) : (
                        <LineChart
                          height={260}
                          margin={{ top: 20, bottom: 40, left: 40, right: 20 }}
                          sx={{
                            "& .MuiChartsAxis-tickLabel": { fill: "#667085", fontSize: 10, fontFamily: "Inter" },
                            "& .MuiChartsAxis-line":      { stroke: "#E5E7EB" },
                            "& .MuiChartsAxis-tick":      { stroke: "#E5E7EB" },
                          }}
                          xAxis={[{
                            scaleType: "point",
                            data: (dashboardStats.enrollmentTrend || [])
                              .slice(-trendDays)
                              .map(d => {
                                const parts = d.date.split('-');
                                return `${parts[2]}/${parts[1]}`;
                              })
                          }]}
                          series={[{
                            data: (dashboardStats.enrollmentTrend || []).slice(-trendDays).map(d => d.count),
                            color: BRAND_PRIMARY,
                            label: "Candidates",
                            showMark: true,
                          }]}
                        />
                      )}
                    </div>
                  </div>

                  {/* Right Column: Project Distribution Donut */}
                  <div className="premium-card p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Project Distribution</h3>
                      <p className="text-xs text-gray-500">Enrollment share across training projects</p>
                    </div>

                    <div className="relative w-full h-[180px] flex items-center justify-center">
                      {dashboardStats.projectChart?.length === 0 ? (
                        <div className="text-xs text-gray-400">No project data recorded</div>
                      ) : (
                        <>
                          <PieChart
                            height={180}
                            sx={{
                              "& .MuiChartsLegend-root": { display: "none" }
                            }}
                            series={[{
                              data: (dashboardStats.projectChart || []).map((p, idx) => ({
                                id: idx,
                                value: p.value,
                                label: p.label
                              })),
                              innerRadius: 58,
                              paddingAngle: 3,
                              cornerRadius: 4,
                              colors: ["#2BB5CE", "#16879A", "#6366F1", "#A855F7", "#EC4899", "#3B82F6"]
                            }]}
                          />
                          <div className="absolute flex flex-col items-center justify-center">
                            <span className="text-[10px] uppercase font-bold text-gray-400">Total</span>
                            <span className="text-lg font-black text-gray-900 leading-tight">
                              {dashboardStats.totalCandidates.toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Legend */}
                    <div className="grid grid-cols-2 gap-2 text-xs border-t border-gray-100 pt-3">
                      {(dashboardStats.projectChart || []).slice(0, 4).map((p, idx) => {
                        const colors = ["#2BB5CE", "#16879A", "#6366F1", "#A855F7"];
                        const share = dashboardStats.totalCandidates ? Math.round((p.value / dashboardStats.totalCandidates) * 100) : 0;
                        return (
                          <div key={idx} className="flex items-center gap-1.5 min-w-0">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }} />
                            <span className="text-gray-600 truncate flex-1 leading-none">{p.label}</span>
                            <span className="font-bold text-gray-800 leading-none">{share}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 3. TOP PERFORMING CENTERS & CANDIDATE STATUS */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Top Centers List (Horizontal custom bars) */}
                  <div className="premium-card p-5 lg:col-span-2">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Top Performing Centers</h3>
                    <p className="text-xs text-gray-500 mb-4">Leading training centers sorted by total enrollment volume</p>

                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                      {!dashboardStats.topPerformingCenters || dashboardStats.topPerformingCenters.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-6">No center records yet</div>
                      ) : (
                        dashboardStats.topPerformingCenters.map((center, idx) => {
                          const maxEnroll = dashboardStats.topPerformingCenters[0]?.candidates || 1;
                          const ratio = Math.max(5, (center.candidates / maxEnroll) * 100);
                          return (
                            <div key={center.centerId || idx} className="space-y-1 cursor-pointer" onClick={() => handleCenterClick({ _id: center.centerId, name: center.centerName })}>
                              <div className="flex justify-between items-center text-xs">
                                <span className="font-bold text-gray-900">{center.centerName}</span>
                                <div className="space-x-2">
                                  <span className="font-semibold text-gray-600">{center.candidates} candidates</span>
                                  <span className="font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">{center.approvalRate}% Approved</span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden">
                                <div className="bg-[#2BB5CE] h-full rounded-full" style={{ width: `${ratio}%` }} />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Candidate Status donut */}
                  <div className="premium-card p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Candidate Status</h3>
                      <p className="text-xs text-gray-500">Approval vs verification status split</p>
                    </div>

                    <div className="relative w-full h-[180px] flex items-center justify-center">
                      <PieChart
                        height={180}
                        sx={{
                          "& .MuiChartsLegend-root": { display: "none" }
                        }}
                        series={[{
                          data: [
                            { id: 0, value: candidateStatus.Approved, label: "Approved" },
                            { id: 1, value: candidateStatus.Pending, label: "Pending" },
                            { id: 2, value: candidateStatus.Rejected, label: "Rejected" },
                          ],
                          innerRadius: 58,
                          paddingAngle: 3,
                          cornerRadius: 4,
                          colors: [COLOR_SUCCESS, COLOR_WARNING, COLOR_DANGER]
                        }]}
                      />
                      <div className="absolute flex flex-col items-center justify-center">
                        <span className="text-[10px] uppercase font-bold text-gray-400">Total</span>
                        <span className="text-lg font-black text-gray-900 leading-tight">
                          {dashboardStats.totalCandidates.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1.5 border-t border-gray-100 pt-3">
                      {["Approved", "Pending", "Rejected"].map((status, idx) => {
                        const colors = [COLOR_SUCCESS, COLOR_WARNING, COLOR_DANGER];
                        const count = candidateStatus[status];
                        const pctVal = dashboardStats.totalCandidates ? Math.round((count / dashboardStats.totalCandidates) * 100) : 0;
                        return (
                          <div key={status} className="flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[idx] }} />
                              <span className="font-semibold text-gray-600">{status}</span>
                            </div>
                            <div className="space-x-1.5">
                              <span className="font-bold text-gray-800">{count}</span>
                              <span className="text-gray-400">({pctVal}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 4. LIVE UTILISATION SECTION */}
                <div className="premium-card p-5">
                  <div className="flex justify-between items-start border-b border-gray-100 pb-3 mb-4">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Live Centre Utilisation</h3>
                      <p className="text-xs text-gray-500">Real-time activity and enrollment performance across centers today</p>
                    </div>
                    <span className="bg-emerald-50 text-emerald-700 font-bold text-[10px] border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-wider animate-pulse flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Live Summary
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Active Centers</span>
                      <span className="text-2xl font-extrabold text-gray-900 mt-1 block">{dashboardStats.activeCentersCount}</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Candidates Today</span>
                      <span className="text-2xl font-extrabold text-[#2BB5CE] mt-1 block">{dashboardStats.todayCandidatesCount}</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Total OCR Today</span>
                      <span className="text-2xl font-extrabold text-indigo-600 mt-1 block">{dashboardStats.todayOcrCount}</span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Avg Processing Today</span>
                      <span className="text-2xl font-extrabold text-gray-900 mt-1 block">{formatDuration(dashboardStats.todayAverageTime)}</span>
                    </div>
                  </div>

                  {/* Horizontal Bar Chart for Centre Utilisation */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Centre Utilisation Graph</h4>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                      {(dashboardStats.centers || []).map((center, idx) => (
                        <div key={center.centerId || idx} className="flex items-center justify-between gap-4 py-1.5 border-b border-gray-50 last:border-b-0">
                          <div className="w-1/4 text-xs font-bold text-gray-800 truncate" title={center.centerName}>
                            {center.centerName}
                          </div>
                          <div className="flex-1 flex items-center gap-3">
                            <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                              <div className="bg-[#2BB5CE] h-full" style={{ width: `${center.utilization}%` }} />
                            </div>
                            <span className="text-xs font-bold text-gray-700 w-8">{center.utilization}%</span>
                          </div>
                          <div className="text-right text-xs text-gray-500 font-semibold w-24">
                            {center.candidates} candidates
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 5. AVERAGE PROCESSING TIME & TIMELINE INDEX */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Processing Time KPI & Comparative */}
                  <div className="premium-card p-5 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Enrollment Processing Time</h3>
                      <p className="text-xs text-gray-500">Average time to complete candidate registry</p>
                    </div>

                    <div className="py-6 text-center">
                      <div className="text-xs uppercase font-extrabold text-gray-400 tracking-wider">Overall Average</div>
                      <div className="text-5xl font-black text-gray-900 tracking-tight mt-1">
                        {formatDuration(dashboardStats.overallAverageTime)}
                      </div>
                      
                      {/* Comparative logic */}
                      {dashboardStats.todayAverageTime > 0 && dashboardStats.yesterdayAverageTime > 0 && (
                        <div className="mt-4 flex items-center justify-center gap-1.5">
                          {dashboardStats.todayAverageTime < dashboardStats.yesterdayAverageTime ? (
                            <>
                              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                                ↓ {formatDuration(dashboardStats.yesterdayAverageTime - dashboardStats.todayAverageTime)} faster than yesterday
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                                ↑ {formatDuration(dashboardStats.todayAverageTime - dashboardStats.yesterdayAverageTime)} slower than yesterday
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 text-xs">
                      <div>
                        <span className="text-gray-400 block">Today's Average</span>
                        <span className="font-bold text-gray-800 text-sm mt-0.5 block">{formatDuration(dashboardStats.todayAverageTime)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400 block">Yesterday's Average</span>
                        <span className="font-bold text-gray-800 text-sm mt-0.5 block">{formatDuration(dashboardStats.yesterdayAverageTime)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Center-wise Processing Time list */}
                  <div className="premium-card p-5 lg:col-span-2">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Center-wise Processing Time</h3>
                    <p className="text-xs text-gray-500 mb-4">Average enrollment time comparison across all centers today (Fastest to Slowest)</p>

                    <div className="space-y-3.5 max-h-[300px] overflow-y-auto pr-2">
                      {centersByTime.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-6">No processing logs today</div>
                      ) : (
                        centersByTime.map((center, idx) => {
                          const isSelected = selectedBranch?.centerId === center.centerId;
                          const maxTime = Math.max(...centersByTime.map(c => c.averageTime), 1);
                          const ratio = Math.max(5, (center.averageTime / maxTime) * 100);
                          return (
                            <div
                              key={center.centerId || idx}
                              onClick={() => handleTimeCenterSelect(center)}
                              className={`p-2 rounded-lg border transition cursor-pointer flex items-center justify-between gap-4 ${
                                isSelected
                                  ? 'border-[#2BB5CE] bg-[#E6F7FA]/30 shadow-sm'
                                  : 'border-transparent hover:bg-gray-50'
                              }`}
                            >
                              <div className="w-1/4 text-xs font-bold text-gray-800 truncate">
                                {center.centerName}
                              </div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 bg-gray-100 h-2.5 rounded-full overflow-hidden">
                                  <div className="bg-[#16879A] h-full rounded-full" style={{ width: `${ratio}%` }} />
                                </div>
                                <span className="text-xs font-bold text-gray-800 w-12 text-right">
                                  {formatDuration(center.averageTime)}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {/* 6. INDIVIDUAL CENTER DETAIL EXPANSION PANEL */}
                {selectedBranch && centerDetailData && (
                  <div className="premium-card p-5 border-[#2BB5CE] bg-[#E6F7FA]/10">
                    <div className="flex justify-between items-start border-b border-[#2BB5CE]33 pb-3 mb-4">
                      <div>
                        <h3 className="text-base font-extrabold text-gray-900">{centerDetailData.centerName?.toUpperCase()}</h3>
                        <p className="text-xs text-gray-500">{centerDetailData.location} · Daily Performance Analysis</p>
                      </div>
                      <button
                        className="text-xs font-bold text-gray-400 hover:text-gray-600 cursor-pointer"
                        onClick={() => setSelectedBranch(null)}
                      >
                        Close Detail View
                      </button>
                    </div>

                    {loadingDetail ? (
                      <div className="flex justify-center items-center py-10">
                        <CircularProgress size={24} />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Summary details */}
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Enrollment Time</span>
                              <span className="text-lg font-black text-gray-800 block mt-1">
                                {formatDuration(centerDetailData.averageTime)}
                              </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Total Enrolled</span>
                              <span className="text-lg font-black text-gray-800 block mt-1">
                                {centerDetailData.totalEnrollments}
                              </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Total OCR</span>
                              <span className="text-lg font-black text-gray-800 block mt-1">
                                {centerDetailData.ocrCount}
                              </span>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Approved / Pending</span>
                              <span className="text-xs font-bold text-gray-800 block mt-1 leading-tight">
                                <span className="text-emerald-600">{centerDetailData.approved} ok</span>
                                <span className="text-gray-300 mx-1">/</span>
                                <span className="text-amber-600">{centerDetailData.pending} wait</span>
                              </span>
                            </div>
                          </div>
                          <div className="bg-white p-3.5 rounded-lg border border-gray-100 shadow-sm text-xs space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Peak Activity:</span>
                              <span className="font-bold text-gray-800">{centerDetailData.peakActivityHour}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Last Active:</span>
                              <span className="font-semibold text-gray-700">
                                {centerDetailData.lastActive ? new Date(centerDetailData.lastActive).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Hourly Processing Time chronological line chart */}
                        <div className="lg:col-span-2 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-2">Hourly Processing Time</span>
                          <div className="w-full h-[180px]">
                            {centerDetailData.hourlyData?.length === 0 ? (
                              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                                No hourly activity logged
                              </div>
                            ) : (
                              <LineChart
                                height={180}
                                margin={{ top: 20, bottom: 30, left: 40, right: 10 }}
                                sx={{
                                  "& .MuiChartsAxis-tickLabel": { fill: "#667085", fontSize: 9, fontFamily: "Inter" }
                                }}
                                xAxis={[{
                                  scaleType: "point",
                                  data: centerDetailData.hourlyData.map(d => d.hour)
                                }]}
                                series={[{
                                  data: centerDetailData.hourlyData.map(d => d.averageTime),
                                  color: "#16879A",
                                  label: "Duration (sec)"
                                }]}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 7. PROCESSING TIME TREND (30 DAYS LINE CHART) */}
                <div className="premium-card p-5">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Processing Time Trend</h3>
                      <p className="text-xs text-gray-500">Average enrollment time trend line</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className={`trend-filter-btn ${timeTrendDays === 7 ? 'active' : ''}`}
                        onClick={() => setTimeTrendDays(7)}
                      >
                        7 Days
                      </button>
                      <button
                        className={`trend-filter-btn ${timeTrendDays === 30 ? 'active' : ''}`}
                        onClick={() => setTimeTrendDays(30)}
                      >
                        30 Days
                      </button>
                    </div>
                  </div>

                  <div className="w-full h-[240px]">
                    {dashboardStats.trend?.length === 0 ? (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                        No processing time trend records
                      </div>
                    ) : (
                      <LineChart
                        height={220}
                        margin={{ top: 20, bottom: 40, left: 45, right: 20 }}
                        sx={{
                          "& .MuiChartsAxis-tickLabel": { fill: "#667085", fontSize: 10, fontFamily: "Inter" }
                        }}
                        xAxis={[{
                          scaleType: "point",
                          data: (dashboardStats.trend || []).slice(-timeTrendDays).map(d => {
                            const parts = d.date.split('-');
                            return `${parts[2]}/${parts[1]}`;
                          })
                        }]}
                        series={[{
                          data: (dashboardStats.trend || []).slice(-timeTrendDays).map(d => d.averageTime),
                          color: BRAND_PRIMARY,
                          label: "Avg Time (sec)"
                        }]}
                      />
                    )}
                  </div>
                </div>

                {/* 8. LIVE CENTER TABLE */}
                <div className="premium-card overflow-hidden">
                  <div className="p-5 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Live Center Activity</h3>
                    <p className="text-xs text-gray-500">Training center user sessions and today's volume index</p>
                  </div>

                  <div className="overflow-x-auto w-full">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th align="left">Center / User</th>
                          <th align="left">Location</th>
                          <th align="left">Role Scope</th>
                          <th align="left">Login Time</th>
                          <th align="left">Last Active</th>
                          <th align="center">Candidates</th>
                          <th align="center">OCR</th>
                          <th align="center">Avg Time</th>
                          <th align="center">Status</th>
                          <th align="center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const activeCenters = (dashboardStats.centers || []).filter(center => getCenterStatus(center).label !== "OFFLINE");
                          if (activeCenters.length === 0) {
                            return (
                              <tr>
                                <td colSpan={11} align="center" className="py-8 text-gray-400 font-semibold text-xs uppercase tracking-wider">
                                  No active centers online today
                                </td>
                              </tr>
                            );
                          }
                          return activeCenters.map((center, idx) => {
                            const status = getCenterStatus(center);
                            return (
                              <tr key={center.centerId || idx}>
                                <td className="font-mono text-xs font-bold text-gray-400">
                                  {(idx + 1).toString().padStart(2, '0')}
                                </td>
                                <td align="left">
                                  <span className="font-extrabold text-gray-900 block">{center.centerName}</span>
                                </td>
                                <td align="left" className="text-gray-500">
                                  {center.location || "N/A"}
                                </td>
                                <td align="left" className="font-semibold text-gray-700 capitalize">
                                  {center.role === "branchUser" ? "Center Staff" : center.role || "Center Staff"}
                                </td>
                                <td align="left" className="text-gray-500 font-mono text-[12.5px]">
                                  {center.loginTime ? new Date(center.loginTime).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : "—"}
                                </td>
                                <td align="left" className="text-gray-500 font-mono text-[12.5px]">
                                  {center.lastActivity ? new Date(center.lastActivity).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : "—"}
                                </td>
                                <td align="center" className="font-bold text-[#16879A]">
                                  {center.candidates}
                                </td>
                                <td align="center" className="font-bold text-indigo-600">
                                  {center.ocr}
                                </td>
                                <td align="center" className="font-bold text-gray-800 font-mono text-[13px]">
                                  {formatDuration(center.averageTime)}
                                </td>
                                <td align="center">
                                  <span className={`badge-status ${status.className}`}>
                                    {status.label}
                                  </span>
                                </td>
                                <td align="center">
                                  <button
                                    className="text-xs font-bold text-[#16879A] bg-[#E6F7FA] border border-[#2BB5CE]33 px-3 py-1.5 rounded-md hover:bg-[#2BB5CE] hover:text-white transition cursor-pointer"
                                    onClick={() => navigate("/daily-activity", { state: { autoOpenBranchId: center.centerId } })}
                                  >
                                    View Activity
                                  </button>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

            {/* ══════════ DRILL-DOWN SECTIONS ══════════ */}

            {/* PROJECTS */}
            {view === "projects" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase">Registry · Projects</h3>
                  <p className="text-xs text-gray-500">Select a project to explore its programs</p>
                </div>
                {loadingProjects ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <div key={i} className="animate-pulse bg-white border border-gray-200 rounded-xl h-24" />)}
                  </div>
                ) : projects.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-10">No projects found</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {projects.map((project, idx) => {
                      const norm = PROJECT_NORMALIZE_MAP[project] || project;
                      const c = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                      return (
                        <div
                          key={project}
                          className="premium-card p-5 flex justify-between items-center cursor-pointer transition transform hover:-translate-y-0.5"
                          style={{ borderLeft: `4px solid ${c.border}` }}
                          onClick={() => handleOpenProject(project)}
                        >
                          <div>
                            <span className="font-extrabold text-gray-900 text-base">{project}</span>
                            <p className="text-[11px] text-gray-500 mt-1">Manage partnership courses</p>
                          </div>
                          <span className="text-xs font-bold text-white bg-slate-900 rounded-full px-3 py-1 font-mono">
                            {projectCounts[norm] || 0}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* PROGRAMS */}
            {view === "programs" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase">{selectedProject}</h3>
                  <p className="text-xs text-gray-500">Select a program to view its training locations</p>
                </div>
                {programs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-10">No programs available under this project</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {programs.map((prog, idx) => (
                      <div
                        key={prog}
                        className="premium-card p-5 flex justify-between items-center cursor-pointer transition transform hover:-translate-y-0.5"
                        onClick={() => handleProgramClick(prog)}
                      >
                        <div>
                          <span className="font-extrabold text-gray-900 text-base">{prog}</span>
                          <p className="text-[11px] text-gray-500 mt-1">Operational branch course</p>
                        </div>
                        <span className="text-xs font-bold text-gray-600 bg-gray-100 rounded-full px-3 py-1 font-mono">
                          {programCounts[prog] || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* LOCATIONS / CENTERS */}
            {view === "locations" && selectedProgram && (
              <div className="space-y-6">
                <div className="flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 uppercase">{selectedProgram.name}</h3>
                    <p className="text-xs text-gray-500">Active centers mapping candidates registry</p>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center h-[42px] px-3 rounded-lg border border-gray-200 bg-white">
                      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        placeholder="Search centers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="text-xs bg-transparent border-none outline-none text-gray-800 placeholder-gray-400 focus:ring-0"
                      />
                    </div>
                    <div className="flex items-center h-[42px] px-3 rounded-lg border border-gray-200 bg-white">
                      <FunnelIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="text-xs bg-transparent border-none outline-none text-gray-700 cursor-pointer"
                      >
                        <option value="">All Locations</option>
                        {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {loadingLocations ? (
                  <div className="animate-pulse bg-white border border-gray-200 rounded-xl h-[240px]" />
                ) : (
                  <div className="premium-card overflow-hidden">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th align="left">Center Name</th>
                          <th align="left">Location</th>
                          <th align="center">Enrolled</th>
                          <th align="center">Approval Rate</th>
                          <th align="center">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBranches.map(branch => {
                          const stats = branchCounts[branch._id] || { total: 0, approved: 0 };
                          const pending = stats.total - stats.approved;
                          const rate = stats.total ? Math.round((stats.approved / stats.total) * 100) : 0;
                          return (
                            <tr key={branch._id} className="cursor-pointer" onClick={() => handleCenterClick(branch)}>
                              <td align="left" className="font-extrabold text-gray-900">{branch.name}</td>
                              <td align="left" className="text-gray-500">{branch.location}</td>
                              <td align="center" className="font-bold text-[#16879A] font-mono">{stats.total}</td>
                              <td align="center">
                                <span className={`badge-status ${rate >= 80 ? 'badge-active' : 'badge-idle'}`}>
                                  {rate}% ({stats.approved} approved)
                                </span>
                              </td>
                              <td align="center" className="font-bold text-amber-600 font-mono">{pending}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* CENTER CANDIDATES GRID & APPROVAL LEDGER */}
            {view === "centerCandidates" && selectedBranch && (
              <div className="space-y-6">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 uppercase">{selectedBranch.name}</h3>
                    <p className="text-xs text-gray-500">{selectedBranch.location} · {centerCandidates.length} enrolled candidates</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center h-[42px] px-3 rounded-lg border border-gray-200 bg-white">
                      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        placeholder="Search candidates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="text-xs bg-transparent border-none outline-none text-gray-800 placeholder-gray-400 focus:ring-0"
                      />
                    </div>
                    <div className="flex items-center h-[42px] px-3 rounded-lg border border-gray-200 bg-white">
                      <CalendarDaysIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="text-xs bg-transparent border-none outline-none text-gray-700 cursor-pointer"
                      />
                    </div>
                    <span className="bg-[#2BB5CE] text-white font-bold text-xs px-3 py-2 rounded-lg shadow-sm">
                      Today: {todayCount}
                    </span>
                  </div>
                </div>

                <div className="premium-card overflow-hidden">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th align="left">Candidate Name</th>
                        <th align="left">Contact Number</th>
                        <th align="left">Location</th>
                        <th align="left">Enrolled On</th>
                        <th align="center">Approval Status</th>
                        {(role === "admin" || role === "superAdmin") && <th align="center">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCandidates.map(c => (
                        <tr key={c._id}>
                          <td align="left" className="font-extrabold text-gray-900">{c.name?.toUpperCase()}</td>
                          <td align="left" className="font-mono text-gray-600">{c.contactNumber}</td>
                          <td align="left" className="text-gray-500">{c.location}</td>
                          <td align="left" className="text-gray-500 font-mono text-xs">
                            {new Date(c.createdAt).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td align="center">
                            <span className={`badge-status ${
                              c.approvalstatus === "Approved" ? "badge-active" : c.approvalstatus === "Rejected" ? "badge-offline text-red-600" : "badge-idle"
                            }`}>
                              {c.approvalstatus || "Pending"}
                            </span>
                          </td>
                          {(role === "admin" || role === "superAdmin") && (
                            <td align="center">
                              {c.approvalstatus === "Pending" && (
                                <button
                                  className="text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-md hover:bg-indigo-600 hover:text-white transition cursor-pointer"
                                  onClick={() => {
                                    setApprovalCandidate(c);
                                    setApprovalStatus("");
                                    setApprovalReason("");
                                    setApprovalDialog(true);
                                  }}
                                >
                                  Review
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ALL PROGRAMS OVERVIEW */}
            {view === "allPrograms" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 uppercase">All Vocational Programs</h3>
                  <p className="text-xs text-gray-500">Comprehensive programs listing mapped to projects</p>
                </div>

                <div className="space-y-6">
                  {Object.keys(allProgramsByProject).map(proj => {
                    const norm = PROJECT_NORMALIZE_MAP[proj] || proj;
                    const c = PROJECT_COLORS[norm] || DEFAULT_PROJECT_COLOR;
                    return (
                      <div key={proj} className="premium-card overflow-hidden">
                        <div className="p-4 flex justify-between items-center" style={{ backgroundColor: c.bg, borderBottom: `2px solid ${c.border}` }}>
                          <span className="font-extrabold text-base" style={{ color: c.text }}>{proj}</span>
                          <span className="text-xs font-bold text-white bg-slate-900 rounded-full px-2.5 py-0.5">
                            {allProgramsByProject[proj].length} programs
                          </span>
                        </div>
                        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3 bg-white">
                          {allProgramsByProject[proj].map(prog => (
                            <div key={prog} className="border border-gray-200 p-3 rounded-lg text-xs font-semibold text-gray-700 bg-gray-50 shadow-sm text-center">
                              {prog}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

      </div>

      {/* CANDIDATE APPROVAL REVIEW DIALOG */}
      <Dialog
        open={approvalDialog}
        onClose={() => setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17, color: "#111827", borderBottom: `1px solid ${BORDER_COLOR}`, pb: 2 }}>
          Review Enrollment Registry
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <div className="space-y-4">
            <div>
              <span className="text-xs text-gray-400 uppercase font-bold tracking-wider">Candidate Name</span>
              <p className="text-base font-extrabold text-gray-800 mt-0.5">{approvalCandidate?.name?.toUpperCase()}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 uppercase font-bold tracking-wider">Contact Number</span>
              <p className="text-sm font-semibold font-mono text-gray-700 mt-0.5">{approvalCandidate?.contactNumber}</p>
            </div>

            <div className="space-y-1.5">
              <span className="text-xs text-gray-400 uppercase font-bold tracking-wider block">Decision</span>
              <select
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-[13.5px] outline-none focus:border-[#2BB5CE] focus:ring-2 focus:ring-[#2BB5CE]/15 bg-white cursor-pointer font-semibold text-gray-800"
              >
                <option value="">Select Action</option>
                <option value="Approved">Approve Registration</option>
                <option value="Rejected">Reject Registration</option>
              </select>
            </div>

            {approvalStatus === "Rejected" && (
              <div className="space-y-1.5 animate-fadeIn">
                <span className="text-xs text-gray-400 uppercase font-bold tracking-wider block">Rejection Reason</span>
                <textarea
                  value={approvalReason}
                  onChange={(e) => setApprovalReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={3}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-[13.5px] outline-none focus:border-[#2BB5CE] focus:ring-2 focus:ring-[#2BB5CE]/15 bg-white text-gray-800"
                />
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${BORDER_COLOR}` }}>
          <Button
            sx={{ textTransform: "none", color: "#667085", fontWeight: 600 }}
            onClick={() => setApprovalDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!approvalStatus || (approvalStatus === "Rejected" && !approvalReason)}
            onClick={async () => {
              const res = await approveCandidate(approvalCandidate._id, {
                approvalstatus: approvalStatus,
                approvalReason
              });
              if (res.data && res.data.success) {
                toast.success(`Candidate ${approvalStatus} successfully`);
                setApprovalDialog(false);
                if (selectedBranch) {
                  handleCenterClick(selectedBranch);
                } else {
                  fetchDashboardStats();
                }
              } else {
                toast.error("Failed to submit approval decision");
              }
            }}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow: "none",
              backgroundColor: BRAND_PRIMARY,
              "&:hover": { backgroundColor: BRAND_DARK }
            }}
          >
            Submit Decision
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Dashboard;